import { oc } from "@orpc/contract";
import { z } from "zod";
import { SchemaFieldTypeSchema } from "./core/schema-definitions.contract";

const DataAssetVersionSchema = z.object({
  id: z.uuid(),
  dataAssetId: z.uuid(),
  versionNumber: z.number(),
  status: z.enum(["pending_upload", "ready", "error"]),
  reason: z.string().nullable().optional(),
  contentType: z.string(),
  containerName: z.string(),
  blobKey: z.string(),
  fileSize: z.number().nullable().optional(),
  fileName: z.string(),
  uploadedBy: z.uuid(),
  createdAt: z.iso.datetime(),
});

export type DataAssetVersionResponse = z.infer<typeof DataAssetVersionSchema>;

/** Enough of the attached schema definition to name it in the UI. */
const AttachedSchemaDefinitionSchema = z.object({
  id: z.uuid(),
  name: z.string(),
  slug: z.string(),
  version: z.number(),
});

/**
 * A data mapping that sources its rows from the asset, with the schema those
 * rows take their shape from. Mapping flows never attach a schema to the asset
 * itself, so this is what names the schema of a mapping-sourced asset — and
 * where changing it has to happen.
 */
const LinkedDataMappingSchema = z.object({
  id: z.uuid(),
  name: z.string(),
  slug: z.string(),
  schemaDefinition: AttachedSchemaDefinitionSchema.nullable(),
});

export type DataAssetLinkedMappingResponse = z.infer<
  typeof LinkedDataMappingSchema
>;

const DataAssetSchema = z.object({
  id: z.uuid(),
  organizationId: z.uuid(),
  name: z.string(),
  description: z.string().nullable().optional(),
  currentVersionId: z.uuid().nullable().optional(),
  createdBy: z.uuid(),
  metadata: z.record(z.string(), z.unknown()).nullable().optional(),
  schemaDefinitionId: z.uuid().nullable().optional(),
  /**
   * The version that was checked when the schema was attached. When this stops
   * matching `currentVersionId`, a newer version has been uploaded that was
   * never validated.
   */
  schemaValidatedVersionId: z.uuid().nullable().optional(),
  schemaValidatedAt: z.iso.datetime().nullable().optional(),
  schemaDefinition: AttachedSchemaDefinitionSchema.nullable().optional(),
  /** Omitted by the endpoints that don't load mappings; empty when unused. */
  dataMappings: z.array(LinkedDataMappingSchema).optional(),
  createdAt: z.iso.datetime(),
  updatedAt: z.iso.datetime(),
  currentVersion: DataAssetVersionSchema.nullable().optional(),
});

const ValidationIssueSchema = z.object({
  /** 1-based record number, or null for file-level issues (headers, parse errors). */
  record: z.number().nullable(),
  field: z.string().nullable(),
  message: z.string(),
});

const ValidationReportSchema = z.object({
  conforms: z.boolean(),
  format: z.enum(["csv", "json"]),
  recordsChecked: z.number(),
  /** Total issues found, which can exceed `issues.length`. */
  issueCount: z.number(),
  issues: z.array(ValidationIssueSchema),
  /** True when the scan stopped early because too many issues were found. */
  truncated: z.boolean(),
  bytesRead: z.number(),
});

export type DataAssetValidationReportResponse = z.infer<
  typeof ValidationReportSchema
>;

/**
 * A field to validate against, for checks that run before a schema exists.
 * Only what the validator actually reads — nested container types are out of
 * scope here, since this path exists for flat CSV/JSON records.
 */
const ValidationFieldSchema = z.object({
  fieldName: z.string().min(1),
  // The real enum, so an unknown type is rejected at the boundary rather than
  // cast through into the validator.
  fieldType: SchemaFieldTypeSchema,
  isRequired: z.boolean(),
  isMultiple: z.boolean().optional(),
  validationRules: z
    .object({
      min: z.number().optional(),
      max: z.number().optional(),
      pattern: z.string().optional(),
      minLength: z.number().optional(),
      maxLength: z.number().optional(),
      precision: z.number().optional(),
      scale: z.number().optional(),
    })
    .nullable()
    .optional(),
  enumValues: z.array(z.string()).nullable().optional(),
});

export type DataAssetValidationFieldInput = z.infer<
  typeof ValidationFieldSchema
>;

export type DataAssetResponse = z.infer<typeof DataAssetSchema>;

const DataAssetWithVersionsSchema = DataAssetSchema.extend({
  versions: z.array(DataAssetVersionSchema),
});

export type DataAssetWithVersionsResponse = z.infer<
  typeof DataAssetWithVersionsSchema
>;

export const dataAssetContracts = {
  create: oc
    .input(
      z.object({
        name: z.string().min(1).max(255),
        description: z.string().optional(),
        fileName: z.string().min(1),
        contentType: z.string().min(1),
      }),
    )
    .output(
      z.object({
        dataAsset: DataAssetSchema,
        version: DataAssetVersionSchema,
        uploadUrl: z.string(),
      }),
    )
    .route({
      summary: "Create a data asset",
      description:
        "Creates a named data asset (uploaded file such as CSV or JSON) with a pending first version and returns a signed URL to upload the file to.",
      method: "POST",
    }),

  confirmUpload: oc
    .input(
      z.object({
        versionId: z.uuid(),
        fileSize: z.number().optional(),
      }),
    )
    .output(
      z.object({
        dataAsset: DataAssetSchema,
        version: DataAssetVersionSchema,
      }),
    )
    .route({
      summary: "Confirm a data asset's first upload",
      description:
        "Marks the uploaded file of a new data asset's version as ready after the client finishes uploading, making it the current version.",
      method: "POST",
    }),

  list: oc
    .input(z.object({}))
    .output(z.object({ dataAssets: z.array(DataAssetSchema) }))
    .route({
      summary: "List data assets",
      description:
        "Returns the organization's uploaded data assets with current version, attached schema and the data mappings that source rows from each.",
      method: "GET",
    }),

  get: oc
    .input(z.object({ id: z.uuid() }))
    .output(
      z.object({
        dataAsset: DataAssetWithVersionsSchema,
      }),
    )
    .route({
      summary: "Get a data asset with versions",
      description:
        "Returns one data asset with its full version history, attached schema and linked data mappings.",
      method: "GET",
    }),

  update: oc
    .input(
      z.object({
        id: z.uuid(),
        name: z.string().min(1).max(255).optional(),
        description: z.string().nullable().optional(),
      }),
    )
    .output(z.object({ dataAsset: DataAssetSchema }))
    .route({
      summary: "Rename or describe a data asset",
      description:
        "Updates a data asset's name or description; file contents and versions are unchanged.",
      method: "PUT",
    }),

  delete: oc
    .input(z.object({ id: z.uuid() }))
    .output(z.object({ success: z.boolean() }))
    .route({
      summary: "Delete a data asset",
      description: "Deletes a data asset from the organization by ID.",
      method: "DELETE",
    }),

  uploadVersion: oc
    .input(
      z.object({
        dataAssetId: z.uuid(),
        fileName: z.string().min(1),
        contentType: z.string().min(1),
        reason: z.string().optional(),
      }),
    )
    .output(
      z.object({
        version: DataAssetVersionSchema,
        uploadUrl: z.string(),
      }),
    )
    .route({
      summary: "Upload a new data asset version",
      description:
        "Starts a new version of an existing data asset with an optional reason and returns a signed URL for uploading the replacement file.",
      method: "POST",
    }),

  confirmVersionUpload: oc
    .input(
      z.object({
        versionId: z.uuid(),
        fileSize: z.number().optional(),
      }),
    )
    .output(
      z.object({
        dataAsset: DataAssetSchema,
        version: DataAssetVersionSchema,
      }),
    )
    .route({
      summary: "Confirm a new data asset version upload",
      description:
        "Marks a newly uploaded data asset version as ready after the file upload completes, making it the asset's current version.",
      method: "POST",
    }),

  revertVersion: oc
    .input(
      z.object({
        dataAssetId: z.uuid(),
        versionId: z.uuid(),
      }),
    )
    .output(z.object({ dataAsset: DataAssetSchema }))
    .route({
      summary: "Revert a data asset to an earlier version",
      description:
        "Sets a previous ready version as the data asset's current version; no versions are deleted.",
      method: "POST",
    }),

  getVersionSignedUrl: oc
    .input(z.object({ versionId: z.uuid() }))
    .output(z.object({ downloadUrl: z.string() }))
    .route({
      summary: "Get a data asset version download link",
      description:
        "Returns a temporary signed URL for downloading the file of one data asset version.",
      method: "GET",
    }),

  /**
   * Validates the asset's current contents against a schema definition and, if
   * every record conforms, records the attachment. Pass `dryRun` to get the
   * report without changing anything.
   */
  attachSchema: oc
    .input(
      z.object({
        dataAssetId: z.uuid(),
        schemaDefinitionId: z.uuid(),
        dryRun: z.boolean().optional(),
      }),
    )
    .output(
      z.object({
        attached: z.boolean(),
        report: ValidationReportSchema,
        /** Present only when the schema was actually attached. */
        dataAsset: DataAssetSchema.nullable(),
      }),
    )
    .route({
      summary: "Attach a schema to a data asset",
      description:
        "Validates the asset's current file against a schema definition and attaches it only if every record conforms; dry run returns the validation report without saving.",
      method: "POST",
    }),

  detachSchema: oc
    .input(z.object({ dataAssetId: z.uuid() }))
    .output(z.object({ dataAsset: DataAssetSchema }))
    .route({
      summary: "Detach the schema from a data asset",
      description:
        "Removes the attached schema definition from a data asset, leaving its file and versions intact.",
      method: "POST",
    }),

  /**
   * Checks the asset against a field list that hasn't been saved as a schema,
   * so a suggested schema can be proven against the file before the user is
   * asked to keep it.
   */
  validateFields: oc
    .input(
      z.object({
        dataAssetId: z.uuid(),
        fields: z.array(ValidationFieldSchema).min(1),
      }),
    )
    .output(z.object({ report: ValidationReportSchema }))
    .route({
      summary: "Validate a data asset against proposed fields",
      description:
        "Checks the asset's current file against an unsaved list of fields and returns a validation report of issues by record; nothing is saved.",
      method: "POST",
    }),
} as const;
